import { reactive, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { object, string, date, array } from 'yup'
import { useRequestReject } from './useRequestReject'
import { usePoliticalActions } from './usePoliticalActions'
import { usePoliticalActionDialog } from './usePoliticalActionDialog'
import { useNotifications } from '@/services/notification/useNotifications'
import { useDialog } from '@/services/useDialog'
import { useTopData } from '@/services/useHome'
import { useLogs } from '@/services/useLogs'
import { useLoader } from '../useLoader'
import { useError } from '../useError'

// import { useImgUploader } from '@/services/useImgUploader'
import axios from '@/axios'

const tabs = reactive([
  {
    name: '基本情報',
    required: true,
    status: 0,
    fields: [
      'submissionDate',
      'selectedActionTypes',
      'startDate',
      'endDate',
      'memberName',
    ],
  },
  {
    name: '場所·目的',
    required: true,
    status: 0,
    fields: ['place', 'purpose'],
  },
  { name: '宿泊場所', required: true, status: 0, fields: ['remarks'] },
  { name: '事務局より', required: false, status: 0 },
])

const actionTypes = ref([
  { id: 1, name: '研修費', hasAttribute: false },
  { id: 2, name: '調査費', hasAttribute: false },
])

export const useRequestFormTabs = () => {
  return { tabs }
}

export const dateComparison = () => {
  const { currentPoliticalAction } = usePoliticalActions()
  if (currentPoliticalAction.startDate && currentPoliticalAction.endDate) {
    if (currentPoliticalAction.startDate <= currentPoliticalAction.endDate) {
      return true
    } else {
      return false
    }
  } else {
    return true
  }
}

export const selectedActionType = () => {
  const { currentPoliticalAction } = usePoliticalActions()
  const array = []
  currentPoliticalAction.selectedActionTypes.forEach(item => {
    array.push(item.hasAttribute)
  })
  return array.includes(true)
}

const schema = object({
  submissionDate: date()
    .required('必須です')
    .typeError('必須です'),
  selectedActionTypes: array()
    .min(1)
    .required('必須です')
    .test(
      'action_type_validation',
      '活動項目を選択してください',
      selectedActionType,
    ),
  startDate: date()
    .required('必須です')
    .typeError('必須です')
    .test('date_validation', '日程が前後しています', dateComparison),
  endDate: date()
    .typeError('必須です')
    .required('必須です')
    .test('date_validation', '日程が前後しています', dateComparison),
  memberName: string()
    .required('必須です')
    .typeError('参加者の氏名を入力してください'),
  place: string()
    .required('必須です')
    .typeError('場所を入力してください'),
  purpose: string()
    .required('必須です')
    .typeError('目的を入力してください'),
  remarks: string().nullable(),
  files: array(),
})

export const useRequestForm = () => {
  const getBitActionType = array => {
    let bit = 0
    // 1: 研修費 2: 調査費
    if (array.length > 0) {
      array.forEach(item => {
        if (item.name.includes('研修費') && item.hasAttribute == true) {
          bit = bit + 1
        }
        if (item.name.includes('調査費') && item.hasAttribute == true) {
          bit = bit + 2
        }
      })
      return [bit]
    }
    return []
  }

  const { getNotificationsAfterResponse } = useNotifications()
  const { getTimelineAfterResponse } = useLogs()
  const { getTopDataAfterResponse } = useTopData()
  const {
    currentPoliticalAction,
    getActivitiesAfterResponse,
  } = usePoliticalActions()
  const { activityDialog } = usePoliticalActionDialog()
  const { toggleShowLoader } = useLoader()
  const { toggleShowError } = useError()
  const route = useRoute()

  // TODO プレビュー画面へ遷移するときにcurrentに代入しているが、フォーム画面で入力した時に既に代入しているので不要？念の為コメントアウトで残しておく。実際に申請書を作成してみたが必要なさそう。
  // watch(activityDialog, () => {
  //   if (activityDialog.isPreview) {
  //     currentPoliticalAction.submissionDate = submissionDate.value
  //     currentPoliticalAction.startDate = startDate.value
  //     currentPoliticalAction.endDate = endDate.value
  //     currentPoliticalAction.memberName = memberName.value
  //     currentPoliticalAction.actionTypes = [...selectedActionTypes.value]
  //     currentPoliticalAction.place = place.value
  //     currentPoliticalAction.purpose = purpose.value
  //     currentPoliticalAction.remarks = remarks.value
  //   }
  // })

  onMounted(() => {
    for (const x of Array(2).keys()) {
      setStatus(x)
    }
  })

  const { meta, handleSubmit } = useForm({ validationSchema: schema })

  watch(meta, meta => {
    if (meta.valid) {
      activityDialog.isPreviewEnabled = true
    } else {
      activityDialog.isPreviewEnabled = false
    }
  })

  const { value: submissionDate, errorMessage: submissionDateError } = useField(
    'submissionDate',
    undefined,
    {
      initialValue: currentPoliticalAction.submissionDate
        ? new Date(currentPoliticalAction.submissionDate)
        : undefined,
    },
  )

  const {
    value: selectedActionTypes,
    errorMessage: selectedActionTypesError,
  } = useField('selectedActionTypes', undefined, {
    initialValue: currentPoliticalAction.selectedActionTypes || new Array(),
  })

  const { value: startDate, errorMessage: startDateError } = useField(
    'startDate',
    undefined,
    {
      initialValue: currentPoliticalAction.startDate
        ? new Date(currentPoliticalAction.startDate)
        : undefined,
    },
  )

  const { value: endDate, errorMessage: endDateError } = useField(
    'endDate',
    undefined,
    {
      initialValue: currentPoliticalAction.endDate
        ? new Date(currentPoliticalAction.endDate)
        : undefined,
    },
  )

  const { value: memberName, errorMessage: memberNameError } = useField(
    'memberName',
    undefined,
    {
      initialValue: currentPoliticalAction.memberName,
    },
  )

  const { value: place, errorMessage: placeError } = useField(
    'place',
    undefined,
    { initialValue: currentPoliticalAction.place },
  )

  const { value: purpose, errorMessage: purposeError } = useField(
    'purpose',
    undefined,
    { initialValue: currentPoliticalAction.purpose },
  )

  const { value: remarks, errorMessage: remarksError } = useField(
    'remarks',
    undefined,
    { initialValue: currentPoliticalAction.application.remarks },
  )

  const { value: files, errorMessage: filesError } = useField(
    'files',
    undefined,
    { initialValue: currentPoliticalAction.application.images },
  )

  const handleImages = files => {
    if (files.length > 0) {
      tabs[3].required = true
      tabs[3].addFilesBtn = true
    } else {
      tabs[3].required = false
      tabs[3].addFilesBtn = false
    }
  }

  const getSentBackItemMessage = attr => {
    const sentBackList = []
    if (
      currentPoliticalAction.application.option.viewType === 0 ||
      currentPoliticalAction.application.option.viewType === 1 ||
      currentPoliticalAction.application.sentBackItems.length == 0
    ) {
      return []
    }
    currentPoliticalAction.application.sentBackItems.forEach(item => {
      if (!item || item.length == 0) return []
      const backItem = item.items.find(item => item.target == attr)
      if (backItem !== undefined) {
        sentBackList.push(backItem)
      }
    })
    if (sentBackList.length > 0) {
      sentBackList
      return sentBackList
    }
    return []
  }

  // 政務活動ダイアログ「入力を確認する」ボタンを押したときにバリデーションを更新する。エラーがなければプレビュー画面へ
  const requestSubmit = handleSubmit(() => {
    activityDialog.isPreview = true
  })

  watch(submissionDate, () => {
    currentPoliticalAction.submissionDate = submissionDate.value
    setStatus(0)
  })
  watch(
    selectedActionTypes,
    () => {
      currentPoliticalAction.selectedActionTypes = selectedActionTypes.value
      setStatus(0)
    },
    { deep: true },
  )
  watch(startDate, () => {
    currentPoliticalAction.startDate = startDate.value
    setStatus(0)
  })
  watch(endDate, () => {
    currentPoliticalAction.endDate = endDate.value
    setStatus(0)
  })
  watch(memberName, () => {
    currentPoliticalAction.memberName = memberName.value
    setStatus(0)
  })
  watch(place, () => {
    currentPoliticalAction.place = place.value
    setStatus(1)
  })
  watch(purpose, () => {
    currentPoliticalAction.purpose = purpose.value
    setStatus(1)
  })
  watch(remarks, () => {
    currentPoliticalAction.application.remarks = remarks.value
    setStatus(2)
  })

  // validateのタイミングでタブのstatusを更新する
  watch(submissionDateError, () => {
    setStatus(0)
  })
  watch(selectedActionTypesError, () => {
    setStatus(0)
  })
  watch(startDateError, () => {
    setStatus(0)
  })
  watch(endDateError, () => {
    setStatus(0)
  })
  watch(memberNameError, () => {
    setStatus(0)
  })
  watch(placeError, () => {
    setStatus(1)
  })
  watch(purposeError, () => {
    setStatus(1)
  })

  const setStatus = index => {
    const err = error(index)
    const inp = input(index)
    if (err && inp) {
      tabs[index].status = 1
    } else if (err && !inp) {
      tabs[index].status = 0
    } else if (!err) {
      tabs[index].status = -1
    }
  }

  // エラーがある時falseを返す
  const error = index => {
    let err = false

    if (index === 0) {
      err =
        typeof submissionDateError.value === 'undefined' &&
        typeof selectedActionTypesError.value === 'undefined' &&
        typeof startDateError.value === 'undefined' &&
        typeof endDateError.value === 'undefined' &&
        typeof memberNameError.value === 'undefined'
    }
    if (index === 1) {
      err =
        typeof placeError.value === 'undefined' &&
        typeof purposeError.value === 'undefined'
    }
    if (index === 2) {
      err = true
    }
    return err
  }

  const input = index => {
    let inp = false
    if (index === 0) {
      inp =
        submissionDate.value &&
        getBitActionType(selectedActionTypes.value) !== 0 &&
        startDate.value &&
        endDate.value &&
        memberName.value
    }
    if (index === 1) {
      inp = place.value && purpose.value
    }
    if (index === 2) {
      inp = remarks.value
    }
    return inp
  }

  const submitActivity = async (id, type) => {
    try {
      toggleShowLoader(true)
      if (!(type === 'application' || type === 'application-apply')) {
        throw new Error('invalid type: ' + type)
      }
      const payload = {
        is_temporary: type === 'application',
        document_type: currentPoliticalAction.application.documentType,
        action_types: getBitActionType(
          currentPoliticalAction.selectedActionTypes,
        ),
        start_date: currentPoliticalAction.startDate,
        end_date: currentPoliticalAction.endDate,
        place: currentPoliticalAction.place,
        member_name: currentPoliticalAction.memberName,
        purpose: currentPoliticalAction.purpose,
        remarks: currentPoliticalAction.application.remarks,
        submission_date: currentPoliticalAction.submissionDate,
        images: currentPoliticalAction.application.images,
      }
      const isPost = id === null
      const res = isPost
        ? await axios.post(`/actions`, payload)
        : await axios.put(`/actions/${id}`, payload)
      if (route.name === 'Activities') {
        await getActivitiesAfterResponse(res)
      } else if (route.name !== 'Activities') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(res)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(res)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(res)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const cancelRequest = async (id, type) => {
    try {
      toggleShowLoader(true)
      const res = await axios.post(`/actions/cancel`, {
        document_id: id,
        document_type: type,
      })
      if (route.name === 'Activities') {
        await getActivitiesAfterResponse(res)
      } else if (route.name !== 'Activities') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(res)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(res)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(res)
        }
      }

      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const approveRequest = async (id, documentId, type, userRank) => {
    try {
      toggleShowLoader(true)
      const { rejectedAttrs, toSnake } = useRequestReject()
      let data = {}
      // 会派事務局の時にアップデートを行う
      if (userRank === 2) {
        const payload = {
          is_temporary: false,
          document_type: currentPoliticalAction.application.documentType,
          action_types: getBitActionType(
            currentPoliticalAction.selectedActionTypes,
          ),
          start_date: currentPoliticalAction.startDate,
          end_date: currentPoliticalAction.endDate,
          place: currentPoliticalAction.place,
          member_name: currentPoliticalAction.memberName,
          purpose: currentPoliticalAction.purpose,
          remarks: currentPoliticalAction.application.remarks,
          submission_date: currentPoliticalAction.submissionDate,
          images: currentPoliticalAction.application.images,
        }
        await axios.put(`/actions/${id}`, payload)
      }

      if (rejectedAttrs.length !== 0) {
        toSnake()
        data = {
          document_id: documentId,
          document_type: type,
          sent_back_items: rejectedAttrs,
        }
      } else {
        data = {
          document_id: documentId,
          document_type: type,
        }
      }
      const res = await axios.post(`/actions/approve`, data)
      if (route.name === 'Activities') {
        await getActivitiesAfterResponse(res)
      } else if (route.name !== 'Activities') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(res)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(res)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(res)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const resetActionTypes = () => {
    actionTypes.value.forEach(item => {
      item.hasAttribute = false
    })
  }
  return {
    handleImages,
    getSentBackItemMessage,
    submitActivity,
    cancelRequest,
    approveRequest,
    resetActionTypes,
    getBitActionType,
    requestSubmit,
    handleSubmit,
    meta,
    actionTypes,
    submissionDate,
    submissionDateError,
    selectedActionTypes,
    selectedActionTypesError,
    startDate,
    startDateError,
    endDate,
    endDateError,
    memberName,
    memberNameError,
    place,
    placeError,
    purpose,
    purposeError,
    remarks,
    remarksError,
    files,
    filesError,
  }
}
